import type { GapRowType, HiringGapsGrid } from "../types";
import type { GapTone } from "./gapTones";
import { type MetricRow, metricRowOf } from "./metricRows";
import { buildMissingGrid, buildMissingToneFns, cellMissing, cellTerms, gridVal, type HiringGridBundle, type IncludeFlags } from "./missingGrid";

/** Everything one cell of the Recurring Gaps grid is read from. */
export type GapCellContext = {
	grids: HiringGridBundle;
	agencyGrid: HiringGapsGrid;
	includeFlags: IncludeFlags;
	/** `buildMissingToneFns` over every shift the grid knows — filtered or not. */
	toneFns: Record<string, (missing: number) => GapTone>;
};

/**
 * What one cell says, before anything draws it. The grid and its Excel download both render from this, so
 * the rules — a blank Needed or Recurring for 0, `X +Y` for applied hires, a dash for an empty breakdown
 * cell, the gap tone — live in one place and the file cannot drift from the screen.
 */
export type GapCell =
	/** `value` 0 draws blank. */
	| { kind: "needed"; value: number }
	/** `template` 0 with no `applied` draws blank; `applied` > 0 draws `template +applied`. */
	| { kind: "recurring"; template: number; applied: number }
	/** `missing` > 0 is short, < 0 over, 0 exactly covered. `template` is the real coverage behind it. */
	| { kind: "missing"; missing: number; tone: GapTone; template: number }
	/** `value` 0 draws a dash. */
	| { kind: "metric"; metric: MetricRow; value: number };

export const metricGridOf = (context: GapCellContext, key: MetricRow["key"]): HiringGapsGrid => {
	const { grids } = context;
	const byKey: Record<MetricRow["key"], HiringGapsGrid> = {
		structural_ot: grids.structuralOtGrid,
		internal_pickup_ot: grids.internalPickupOtGrid,
		internal_pickup_non_ot: grids.internalPickupNonOtGrid,
		agency: context.agencyGrid,
		absences: grids.absencesGrid,
	};
	return byKey[key];
};

export const resolveGapCell = (context: GapCellContext, rowType: GapRowType, label: string, key: number): GapCell => {
	const { needed, template, applied } = cellTerms(context.grids, context.includeFlags, label, key);
	if (rowType === "needed") {
		return { kind: "needed", value: needed };
	}
	if (rowType === "recurring") {
		return { kind: "recurring", template, applied };
	}
	if (rowType === "missing") {
		const missing = cellMissing(context.grids, context.includeFlags, label, key);
		const toneFn = context.toneFns[label];
		// DEFAULT: neutral for a shift with no tone ranking. Unreachable — the ranking covers every shift the
		// grid can draw — and kept quiet because the grid renders this inside the page's ErrorBoundary.
		return { kind: "missing", missing, tone: toneFn ? toneFn(missing) : "neutral", template };
	}
	const metric = metricRowOf(rowType);
	return { kind: "metric", metric, value: gridVal(metricGridOf(context, rowType), label, key) };
};

/** What the grid prints in a breakdown cell with nothing in it, and for a day with no Template HPPD. */
export const EMPTY_GAP_CELL = "–";

/**
 * What a cell prints, for every row but Missing — Missing is drawn by each surface in its own way (a check
 * icon on screen, a number format in the file), from the same signed `formatMissing` rule. A 0 Needed or
 * Recurring is blank and an empty breakdown cell is a dash, so the numbers that matter stand out.
 */
export const gapCellText = (cell: Exclude<GapCell, { kind: "missing" }>): string | number => {
	if (cell.kind === "needed") {
		return cell.value === 0 ? "" : cell.value;
	}
	if (cell.kind === "recurring") {
		return cell.applied > 0 ? `${cell.template} +${cell.applied}` : cell.template === 0 ? "" : cell.template;
	}
	return cell.value > 0 ? cell.value : EMPTY_GAP_CELL;
};

/** The context for one render of the grid. Tones are ranked over every shift, filtered or not. */
export const buildGapCellContext = (grids: HiringGridBundle, agencyGrid: HiringGapsGrid, includeFlags: IncludeFlags, gridWeeks: number): GapCellContext => ({
	grids,
	agencyGrid,
	includeFlags,
	toneFns: buildMissingToneFns(buildMissingGrid(grids, includeFlags, gridWeeks)),
});
